import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useQuery } from '@tanstack/react-query'
import {
  getMerchandisingTagGroups,
  type MerchandisingTagIndexQueryKeyEntries,
} from './merchandising-tags.api'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries `getMerchandisingTagGroups` reads; `merchandisingTagQueryKeys.index` sets them. */
type MerchandisingTagIndexQueryKey = [AdminQueryRoot & MerchandisingTagIndexQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const merchandisingTagQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'merchandising-tag' }, { organizationId }),
  ],
  index: ({ organizationId }: OrganizationScope = {}): MerchandisingTagIndexQueryKey =>
    buildScopedAdminQueryKey(merchandisingTagQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
}

export const merchandisingTagQueries = {
  index: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: merchandisingTagQueryKeys.index({ organizationId }),
      queryFn: getMerchandisingTagGroups,
      staleTime: 1000 * 60 * 15,
    }),
}

export function useMerchandisingTagGroups({
  organizationId,
  params = {},
}: OrganizationScope & {
  params?: QueryParams<ReturnType<typeof merchandisingTagQueries.index>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...merchandisingTagQueries.index({ organizationId: resolvedOrganizationId }),
    placeholderData: [],
    ...params,
  })
}
